import React, { useCallback, useEffect, useMemo } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import { faChevronLeft, faChevronRight, faCog } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEllipsisV, faPaperPlane, faSpinnerThird, faSync } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import EventEmitter from "events";

import { isSalesforceFieldPrefillFeatureFlagOn } from "~/account/Integrations/Salesforce/featureFlag";
import { showFeatureModal } from "~/billing";
import AppContext from "~/common/AppContext";
import { confirm } from "~/lib/prompts";
import { getFirstUserFacingError } from "~/lib/utils";
import ObjectCard, { type SalesforceObjectRecordType } from "~/meetings/SeriesSidePanel/CRMPanel/Salesforce/ObjectCard";
import { EmptyState } from "~/meetings/SeriesSidePanel/EmptyState";
import { SidePanelItem } from "~/meetings/SeriesSidePanel/SidePanelItem";
import { environment } from "~/models";
import { Button, LoadingSpinner, Tooltip } from "~/ui";
import { ButtonLink, ContactSupportLink } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";
import toast from "~/ui/toast";

import AiSuggestionsSelector, { type OptionType } from "./AiSuggestionsSelector";
import SalesforceSendNotesModal from "./SalesforceSendNotes";

import type { SalesforcePanelGetAiSuggestionsQuery } from "~/__generated__/SalesforcePanelGetAiSuggestionsQuery.graphql";
import type { SalesforcePanelQuery } from "~/__generated__/SalesforcePanelQuery.graphql";
import type { SalesforcePanelUpdateRecordsMutation } from "~/__generated__/SalesforcePanelUpdateRecordsMutation.graphql";
import type { SalesforceInstanceType } from "~/__generated__/SendNotesModal_viewer.graphql";

import styles from "./SalesforcePanel.module.css";

export const salesforcePanelEmitter = new EventEmitter();
export const ACCEPT_ALL_AI_SUGGESTIONS_EVENT = "ACCEPT_ALL_AI_SUGGESTIONS";

const salesforceQuery = graphql`
	query SalesforcePanelQuery($streamId: ID!) {
		salesforceContactsForStream: salesforceObjectsForStream(streamId: $streamId) {
			__typename
			name
			label
			value
			id
			url

			fields {
				name
				label
				value
				type
				interactionType
				length
			}

			references {
				name
				label
				value
				id
				url

				fields {
					name
					label
					value
					type
					interactionType
					length
				}
			}
		}
	}
`;

const salesforceUpdateRecordsMutation = graphql`
	mutation SalesforcePanelUpdateRecordsMutation($input: SalesforceUpdateRecordsInput!) {
		salesforceUpdateRecords(input: $input) {
			updatedRecords {
				success
				errors {
					message
				}
				objectName
				fieldName
			}
		}
	}
`;

const salesforceGetAiSuggestionsQuery = graphql`
	query SalesforcePanelGetAiSuggestionsQuery(
		$noteId: String!
		$selectedRecords: [SalesforceSelectedRecordForAiSuggestions!]!
	) {
		salesforceGetAiSuggestions(noteId: $noteId, selectedRecords: $selectedRecords) {
			suggestions {
				objectLabel
				recordId
				fields {
					fieldLabel
					suggestedValue
				}
			}
		}
	}
`;

export type SalesforceAiSuggestionsFieldDictType = {
	[fieldLabel: string]: string;
};

type SalesforceAiSuggestionsDictType = {
	[recordId: string]: SalesforceAiSuggestionsFieldDictType;
};

function Loading({ generatingAiSuggestions }: { generatingAiSuggestions: boolean }) {
	return (
		<div className={styles.loading}>
			<p className={styles.loadingText}>
				{generatingAiSuggestions ? "Generating AI suggestions..." : "Loading Salesforce data..."}
			</p>
			<FontAwesomeIcon className={styles.icon} icon={faSpinnerThird} spin />
		</div>
	);
}

function Error({ openIntegrationSettings }: { openIntegrationSettings: () => void }) {
	return (
		<div className={styles.emptyState}>
			<h2>Unable to fetch</h2>
			<p>
				Please try again later. If this issue persists, please try reconnecting your Salesforce account in{" "}
				<ButtonLink onClick={openIntegrationSettings}>Integration settings</ButtonLink> or{" "}
				<ContactSupportLink>contact support</ContactSupportLink>.
			</p>
		</div>
	);
}

function NoneFound({ refetch }: { refetch: () => void }) {
	return (
		<EmptyState
			title="No Salesforce users found"
			description="We were unable to find any Salesforce users for this series"
			buttonText="Refetch"
			buttonOnClick={refetch}
			buttonIcon={faSync}
		/>
	);
}

interface SalesforceInnerPaneProps {
	activeContact: SalesforceObjectRecordType;
	handleRecordChange: (
		objectName: string,
		recordId: string,
		fieldName: string,
		originalValue: string,
		newValue: string,
	) => void;
	undoTrigger: number;
	aiSuggestions: SalesforceAiSuggestionsDictType;
}

function SalesforceInnerPane({
	activeContact,
	handleRecordChange,
	undoTrigger,
	aiSuggestions,
}: SalesforceInnerPaneProps) {
	const hasAccessToSalesforcePrefilling = isSalesforceFieldPrefillFeatureFlagOn();

	return (
		<>
			<ObjectCard
				{...activeContact}
				canOpenDetails
				handleRecordChange={handleRecordChange}
				undoTrigger={undoTrigger}
				aiSuggestions={aiSuggestions[activeContact.id]}
				isOpen={Object.keys(aiSuggestions).length ? aiSuggestions[activeContact.id] !== undefined : undefined}
			/>
			{activeContact.references.map(reference => (
				<ObjectCard
					key={reference.id}
					{...reference}
					canOpenDetails={reference.name === "Account" ? hasAccessToSalesforcePrefilling : true}
					handleRecordChange={handleRecordChange}
					undoTrigger={undoTrigger}
					aiSuggestions={aiSuggestions[reference.id]}
					isOpen={Object.keys(aiSuggestions).length ? aiSuggestions[reference.id] !== undefined : undefined}
				/>
			))}
		</>
	);
}

interface PrivateProps {
	streamId: string;
	noteId: string;
	hasAccessToSalesforceIntegration: boolean;
	onClose?: () => void;
	lastTypeUsed?: SalesforceInstanceType;
	enhancedNotesEnabled?: boolean;
}

export const SalesforcePanel = ({
	streamId,
	noteId,
	hasAccessToSalesforceIntegration,
	lastTypeUsed,
	enhancedNotesEnabled,
	onClose,
}: PrivateProps): JSX.Element => {
	const [contacts, setContacts] = React.useState<readonly SalesforceObjectRecordType[]>(() => []);
	const [loading, setLoading] = React.useState(true);
	const [generatingAiSuggestions, setGeneratingAiSuggestions] = React.useState(false);
	const [page, setPage] = React.useState(0);
	const [error, setError] = React.useState<boolean>(false);
	const [sendNotesOpen, setSendNotesOpen] = React.useState(false);
	const [isOptionsMenuOpen, setIsOptionsMenuOpen] = React.useState(false);
	const [recordChanges, setRecordChanges] = React.useState<{
		[objectName: string]: { recordId: string; fields: { [fieldName: string]: string } };
	}>({});
	const hasRecordChanges = Object.keys(recordChanges).length > 0;
	const [aiSuggestions, setAiSuggestions] = React.useState<SalesforceAiSuggestionsDictType>({});
	const hasAiSuggestions = Object.keys(aiSuggestions).length > 0;
	const [undoTrigger, setUndoTrigger] = React.useState(0);
	const showFeature = useCallback(() => showFeatureModal("salesforce_integration"), []);

	const { openSettingsModal, account } = React.useContext(AppContext);
	const aiFeaturesEnabled = account?.allowAiFeatures;

	const openIntegrationSettings = React.useCallback(() => {
		openSettingsModal(["apps-and-integrations", "salesforce"]);
	}, [openSettingsModal]);

	const previous = useCallback(() => {
		if (page > 0) {
			setPage(page - 1);
		}
	}, [page]);

	const next = useCallback(() => {
		if (page < contacts.length - 1) {
			setPage(page + 1);
		}
	}, [page, contacts]);

	const fetchContent = useCallback(
		(noToast = true) => {
			const error = () => {
				setError(true);
				setContacts([]);
				if (!noToast) {
					toast.error("Unable to fetch, please try again later");
				}
			};

			setLoading(true);
			fetchQuery<SalesforcePanelQuery>(environment, salesforceQuery, {
				streamId,
			})
				.toPromise()
				.then(data => {
					setLoading(false);
					if (!data) {
						error();
						return;
					}
					setError(false);
					if (!noToast) {
						toast.success(`Salesforce content updated`);
					}

					setContacts(data.salesforceContactsForStream ?? []);
				})
				.catch(() => {
					setLoading(false);
					error();
					return;
				});
		},
		[streamId],
	);

	const refetchContent = useMemo(() => fetchContent.bind(null, false), [fetchContent]);

	useEffect(() => {
		fetchContent(true);
	}, [fetchContent]);

	const openSendNotes = useCallback(() => {
		if (!hasAccessToSalesforceIntegration) {
			showFeature();
			return;
		}

		setSendNotesOpen(true);
	}, [hasAccessToSalesforceIntegration, showFeature]);

	const closeSendNotes = useCallback(() => {
		setSendNotesOpen(false);
	}, []);

	const toggleOptionsOpen = useCallback(() => setIsOptionsMenuOpen(open => !open), []);

	const activeContact = contacts[page];
	const hasFieldsMarkedAsAiSuggestions =
		activeContact?.fields.some(field => field.interactionType === "AI_SUGGESTION") ||
		activeContact?.references?.some(reference =>
			reference.fields.some(field => field.interactionType === "AI_SUGGESTION"),
		);

	const numRecordChanges = React.useMemo(() => {
		return Object.values(recordChanges).reduce((total, obj) => total + Object.keys(obj.fields).length, 0);
	}, [recordChanges]);

	const onSyncClick = useCallback(() => {
		setLoading(true);

		const input = {
			recordsChanges: Object.entries(recordChanges).map(([objectName, { recordId, fields }]) => ({
				objectName,
				recordId,
				fields: Object.entries(fields).map(([name, value]) => ({ name, value })),
			})),
		};

		commitMutation<SalesforcePanelUpdateRecordsMutation>(environment, {
			mutation: salesforceUpdateRecordsMutation,
			variables: { input },
			onCompleted: (response, errors) => {
				setLoading(false);
				if (errors) {
					toast.error("Failed to update Salesforce records");
					return;
				}

				if (response?.salesforceUpdateRecords?.updatedRecords) {
					const { updatedRecords } = response.salesforceUpdateRecords;
					const atLeastOneSuccess = updatedRecords.some(record => record?.success);

					if (atLeastOneSuccess) {
						toast.success("Salesforce record(s) have been updated");
						setAiSuggestions({});
						fetchContent(true);
					}

					updatedRecords.forEach(record => {
						if (record?.errors) {
							record.errors.forEach(error => {
								toast.error(`${error.message}. Please try again, and contact us if the issue persists.`, {
									autoClose: false,
									title: `Could not sync ${record.fieldName ?? "record"}`,
									closeButton: true,
									closeOnClick: false,
								});
							});
						}
					});
				} else {
					toast.error("Failed to update Salesforce records");
				}
			},
			onError: () => {
				setLoading(false);
				toast.error("Failed to update Salesforce records");
			},
		});
	}, [fetchContent, recordChanges]);

	const handleRecordChange = useCallback(
		(objectName: string, recordId: string, fieldName: string, originalValue: string, newValue: string) => {
			// This logic mirrors what we have in production today but I think there is a bug in how we update state.
			// If there are multiple of the same object type (ie. two Opportunities) we are only able to store 1 in
			// the recordChanges state. Editing a field in Opportunity 1 will add a key to the updated state.
			// If we edit in a field in Opportunity 2, the "Opportunity" key in recordChanges will be overridden
			// with new values from Opportunity 2. This state needs to supoort multiple objects of the same type
			setRecordChanges(currentRecordChanges => {
				const updatedState = {
					...currentRecordChanges,
					[objectName]: {
						...currentRecordChanges[objectName],
						recordId,
						fields: {
							...(currentRecordChanges[objectName]?.fields || {}),
							[fieldName]: newValue,
						},
					},
				};

				// remove field if it's the same as the original value
				if (originalValue === newValue) {
					delete updatedState[objectName].fields[fieldName];
				}

				// remove object if there are no fields
				if (Object.keys(updatedState[objectName].fields).length === 0) {
					delete updatedState[objectName];
				}

				return updatedState;
			});
		},
		[],
	);

	const promptContinueEditing = useCallback(() => {
		confirm("All the changes made to the Salesforce fields will be lost.", {
			yesText: "Continue editing",
			noText: "Discard changes",
			title: "Unsaved changes",
		}).then(confirmed => {
			if (!confirmed) {
				setRecordChanges({});
				setAiSuggestions({});
				setUndoTrigger(v => v + 1); // Increment the trigger to reset the fields
			}
		});
	}, []);

	const onAutoFillAiSuggestions = useCallback(
		(selectedRecords: readonly OptionType[]) => {
			if (!hasAccessToSalesforceIntegration) {
				showFeature();
				return;
			}

			setLoading(true);
			setGeneratingAiSuggestions(true);

			fetchQuery<SalesforcePanelGetAiSuggestionsQuery>(environment, salesforceGetAiSuggestionsQuery, {
				noteId,
				selectedRecords: selectedRecords.map(({ label, value, objectLabel, objectName, fields }) => ({
					recordId: value,
					recordName: label,
					objectLabel,
					objectName,
					fields: fields.map(field => ({ ...field })), // Create a mutable copy of the fields array
				})),
			})
				.toPromise()
				.then(data => {
					setLoading(false);
					setGeneratingAiSuggestions(false);
					if (!data) {
						toast.error("Failed to get AI suggestions");
						return;
					}

					const { suggestions } = data.salesforceGetAiSuggestions;

					if (suggestions?.length === 0 || !suggestions?.some(suggestion => suggestion.fields.length > 0)) {
						toast.info("No suggestions found");
						return;
					}

					// Convert the output to a dictionary for easier access
					const suggestionsDict: SalesforceAiSuggestionsDictType = suggestions.reduce((acc, suggestion) => {
						acc[suggestion.recordId] = suggestion.fields.reduce((fieldsAcc, field) => {
							fieldsAcc[field.fieldLabel] = field.suggestedValue;
							return fieldsAcc;
						}, {} as SalesforceAiSuggestionsFieldDictType);
						return acc;
					}, {} as SalesforceAiSuggestionsDictType);

					setAiSuggestions(suggestionsDict);
				})
				.catch(errors => {
					setLoading(false);
					setGeneratingAiSuggestions(false);
					const userFacingError = getFirstUserFacingError(errors);
					toast.error(userFacingError || "Failed to get AI suggestions");
				});
		},
		[hasAccessToSalesforceIntegration, noteId, showFeature],
	);

	const onAcceptAllAiSuggestionsClick = useCallback(() => {
		salesforcePanelEmitter.emit(ACCEPT_ALL_AI_SUGGESTIONS_EVENT);
	}, []);

	return (
		<SidePanelItem>
			<SidePanelItem.Header
				headerTitle={<SidePanelItem.Title>Salesforce</SidePanelItem.Title>}
				actionsMenu={
					<Menu open={isOptionsMenuOpen} onOpenChange={toggleOptionsOpen}>
						<Menu.Trigger disabled={loading || hasRecordChanges || hasAiSuggestions}>
							<SidePanelItem.HeaderIconButton icon={faEllipsisV} title="Options" />
						</Menu.Trigger>
						<Menu.Content align="end">
							<Menu.Item onSelect={openIntegrationSettings} icon={faCog}>
								Integration settings
							</Menu.Item>
						</Menu.Content>
					</Menu>
				}
				onClose={onClose}
			>
				{!loading && !error && (
					<div className={styles.headerRow}>
						<div className={styles.headerActions}>
							{hasFieldsMarkedAsAiSuggestions && activeContact && aiFeaturesEnabled && (
								<AiSuggestionsSelector
									activeContact={activeContact}
									onAutoFillAiSuggestions={onAutoFillAiSuggestions}
									isDisabled={loading}
									hasAiSuggestions={hasAiSuggestions}
									onAcceptAllAiSuggestionsClick={onAcceptAllAiSuggestionsClick}
								/>
							)}
							<SidePanelItem.HeaderIconButton
								size="small"
								title="Send note to Salesforce"
								onClick={openSendNotes}
								icon={faPaperPlane}
							/>
							<SidePanelItem.HeaderIconButton size="small" title="Refetch" onClick={refetchContent} icon={faSync} />
						</div>
						{contacts.length > 1 && (
							<Tooltip
								content={<div className={styles.tooltip}>Save or discard changes to see other contacts</div>}
								maxWidth={150}
								placement="top"
								disabled={!hasRecordChanges && !hasAiSuggestions}
							>
								<div
									className={cx(styles.contactSelector, {
										[styles.disabled]: loading || hasRecordChanges || hasAiSuggestions,
									})}
								>
									<div className={styles.contactCount}>
										Contact {page + 1}/{contacts.length}
									</div>
									<SidePanelItem.HeaderIconButton
										title="Previous"
										size="small"
										icon={faChevronLeft}
										onClick={previous}
										disabled={page === 0 || loading || hasRecordChanges || hasAiSuggestions}
									/>
									<SidePanelItem.HeaderIconButton
										title="Next"
										size="small"
										icon={faChevronRight}
										onClick={next}
										disabled={page === contacts.length - 1 || loading || hasRecordChanges || hasAiSuggestions}
									/>
								</div>
							</Tooltip>
						)}
					</div>
				)}

				{!loading && hasRecordChanges && (
					<div className={styles.editBanner}>
						<p className={styles.editBannerText}>
							<strong>{numRecordChanges}</strong> unsaved {numRecordChanges === 1 ? "change" : "changes"}
						</p>
						<div className={styles.editBannerActions}>
							<Button
								size="slim"
								variant="secondary"
								className={styles.editBannerButton}
								disabled={loading}
								onClick={promptContinueEditing}
							>
								Discard
							</Button>
							<Button
								size="slim"
								variant="primary"
								className={styles.editBannerButton}
								disabled={!hasRecordChanges || loading}
								onClick={onSyncClick}
							>
								Save
							</Button>
						</div>
					</div>
				)}
			</SidePanelItem.Header>
			<SidePanelItem.Body className={styles.body}>
				<div className={styles.salesforcePanel}>
					{loading ? (
						<Loading generatingAiSuggestions={generatingAiSuggestions} />
					) : error ? (
						<Error openIntegrationSettings={openIntegrationSettings} />
					) : contacts.length < 1 ? (
						<NoneFound refetch={refetchContent} />
					) : (
						activeContact && (
							<SalesforceInnerPane
								activeContact={activeContact}
								aiSuggestions={aiSuggestions}
								handleRecordChange={handleRecordChange}
								undoTrigger={undoTrigger}
							/>
						)
					)}
				</div>
			</SidePanelItem.Body>
			{sendNotesOpen && (
				<React.Suspense fallback={<LoadingSpinner />}>
					<SalesforceSendNotesModal
						noteId={noteId}
						onClose={closeSendNotes}
						options={contacts}
						defaultOption={activeContact}
						modalMode
						lastTypeUsed={lastTypeUsed}
						enhancedNotesEnabled={enhancedNotesEnabled}
					/>
				</React.Suspense>
			)}
		</SidePanelItem>
	);
};
